import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { useUserSetting } from '@ayocore/platform/identity/client/settings/useUserSetting';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { WidgetFrame, type WidgetProps } from '@ayocore/platform/client/widgets';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { getSalesSummary, getSalesSummaryDefaults } from '../../../client/rpc';
import type { SalesSummaryResult } from '../common/types';
import { crmWidgetStyle } from '../../../client/components/Widgets/shared/presentation';
import { SalesSummarySettingsForm } from './SalesSummarySettingsForm';
import { SalesSummaryWidgetController, getSummaryChange } from './SalesSummaryWidgetController';
import { DEFAULT_SALES_SUMMARY_SETTINGS, decodeSalesSummarySettings } from '../common/settings';

const formatDate = (value: string) => value.split('-').reverse().join('.');
const number = (value: number, precision = 1) =>
  new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: precision,
  }).format(value);

function SummaryMetrics({ data }: { data: SalesSummaryResult }) {
  const { current, previous, currency } = data;
  const money = (value: number) =>
    `${new Intl.NumberFormat('ru-RU', {
      notation: Math.abs(value) >= 1_000_000 ? 'compact' : 'standard',
      maximumFractionDigits: Math.abs(value) >= 1_000_000 ? 1 : currency.precision,
    }).format(value)} ${currency.symbol}`;
  const metrics = [
    {
      label: 'Продажи',
      value: money(current.sales),
      change: getSummaryChange(current.sales, previous?.sales ?? null),
      note: 'По закрытию сделки',
    },
    {
      label: 'Поступления',
      value: money(current.receipts),
      change: getSummaryChange(current.receipts, previous?.receipts ?? null),
      note: 'По дате документа оплаты, с налогами',
    },
    {
      label: 'Отгрузки',
      value: money(current.shipments),
      change: getSummaryChange(current.shipments, previous?.shipments ?? null),
      note: 'По дате документа отгрузки, с налогами',
    },
  ];
  return (
    <>
      {current.dealCount === 0 && current.paymentCount === 0 && current.shipmentCount === 0 && (
        <Text size='sm' c='dimmed'>
          За выбранный период нет закрытых сделок, поступлений и отгрузок.
        </Text>
      )}
      <SimpleGrid cols={{ base: 1, xs: 3 }} spacing='sm'>
        {metrics.map(metric => (
          <Box
            key={metric.label}
            p='sm'
            bg='var(--mantine-color-default-hover)'
            style={{ borderRadius: 10 }}
          >
            <Stack gap={6}>
              <Text size='sm' c='dimmed'>
                {metric.label}
              </Text>
              <Text fz={{ base: 24, sm: 28 }} fw={650} lh={1.15}>
                {metric.value}
              </Text>
              {previous && (
                <Badge
                  variant='light'
                  color={
                    metric.change == null || metric.change === 0
                      ? 'gray'
                      : metric.change > 0
                        ? 'teal'
                        : 'red'
                  }
                  size='sm'
                  style={{ alignSelf: 'flex-start' }}
                >
                  {metric.change == null
                    ? '—'
                    : `${metric.change > 0 ? '+' : ''}${number(metric.change)}%`}
                </Badge>
              )}
              <Text size='xs' c='dimmed'>
                {metric.note}
              </Text>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
    </>
  );
}

/** Самостоятельный CRM-виджет: расчёты на сервере, личные настройки в профиле. */
export function SalesSummaryWidget(props: WidgetProps) {
  const userId = useAuthController(state => state.user?.id);
  return userId == null ? null : <UserSalesSummary key={userId} {...props} />;
}

function UserSalesSummary({ title = 'Финансовая сводка', className }: WidgetProps) {
  const [controller] = useState(() => new SalesSummaryWidgetController(getSalesSummary));
  const { data, loading, error } = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot
  );
  const preferences = useUserSetting('crm.widgets.sales-summary', {
    defaultValue: DEFAULT_SALES_SUMMARY_SETTINGS,
    decode: decodeSalesSummarySettings,
    loadDefaults: getSalesSummaryDefaults,
  });
  const { value: settings, setValue: setSettings, ready, saving } = preferences;
  const { open: openSettings, close: closeSettings } = useModal(SalesSummarySettingsForm);
  useEffect(() => closeSettings, [closeSettings]);

  useEffect(() => {
    if (ready) void controller.load(settings);
    return () => controller.cancelLoad();
  }, [controller, settings, ready]);

  const branchName =
    settings.scope === 'branch' && settings.branchId != null
      ? data?.branches.find(branch => branch.id === settings.branchId)?.name
      : null;
  const managerName =
    settings.managerId == null || settings.scope === 'mine'
      ? null
      : data?.managers.find(manager => manager.id === settings.managerId)?.name;

  return (
    <WidgetFrame
      title={title}
      className={className}
      radius='lg'
      style={crmWidgetStyle}
      actions={
        <ActionIcon
          variant='subtle'
          disabled={!ready || saving}
          aria-label='Настройки финансовой сводки'
          onClick={() =>
            openSettings(
              {
                title: 'Настройки финансовой сводки',
                settings,
                options: data,
                onSave: setSettings,
                onReset: preferences.reset,
              },
              { size: 'md' }
            )
          }
        >
          <IconAdjustmentsHorizontal size={18} />
        </ActionIcon>
      }
    >
      {preferences.loading && <Text size='sm'>Загрузка настроек…</Text>}
      {preferences.error && (
        <Stack gap='xs'>
          <Text role='alert' c='red' size='sm'>
            {preferences.error}
          </Text>
          {!ready && (
            <Button size='xs' variant='light' onClick={() => void preferences.reload()}>
              Повторить загрузку настроек
            </Button>
          )}
        </Stack>
      )}
      <Group justify='space-between'>
        <SegmentedControl
          disabled={!ready || saving}
          aria-label='Область данных финансовой сводки'
          size='xs'
          value={settings.scope}
          onChange={value => {
            if (value === 'mine' || value === 'branch' || value === 'all')
              void setSettings({ ...settings, scope: value, branchId: null, managerId: null });
          }}
          data={[
            { label: 'Мои', value: 'mine' },
            { label: 'Филиал', value: 'branch' },
            { label: 'Все', value: 'all' },
          ]}
        />
        <SegmentedControl
          disabled={!ready || saving}
          size='xs'
          value={settings.period}
          onChange={value => {
            if (value === 'month' || value === 'quarter')
              void setSettings({ ...settings, period: value, referenceDate: '' });
          }}
          data={[
            { label: 'Месяц', value: 'month' },
            { label: 'Квартал', value: 'quarter' },
            ...(settings.period === 'custom' ? [{ label: 'Период', value: 'custom' }] : []),
          ]}
        />
      </Group>
      {(branchName || managerName) && (
        <Text size='xs' c='dimmed'>
          {[branchName, managerName].filter(Boolean).join(' · ')}
        </Text>
      )}
      {loading && (
        <Group role='status' gap='xs'>
          <Loader size='xs' />
          <Text size='sm'>Загрузка финансовой сводки…</Text>
        </Group>
      )}
      {error && (
        <Stack gap='xs'>
          <Text role='alert' c='red' size='sm'>
            {error}
          </Text>
          <Button variant='light' size='xs' onClick={() => void controller.load(settings)}>
            Повторить
          </Button>
        </Stack>
      )}
      {data && (
        <>
          <SummaryMetrics data={data} />
          <Text size='xs' c='dimmed'>
            {formatDate(data.period.from)} — {formatDate(data.period.to)}
            {data.previousPeriod
              ? ` · сравнение с ${formatDate(data.previousPeriod.from)} — ${formatDate(data.previousPeriod.to)}`
              : ''}
          </Text>
        </>
      )}
    </WidgetFrame>
  );
}
